import React from "react";
import { Check, ChevronsUpDown, Linkedin, Loader2, Mail, MapPin, MoreHorizontal, Phone, Target, UserPlus } from "lucide-react";
import CommonAvatar from "@/shared/components/ui/Avatar/CommonAvatar";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import TruncatedText from "@/shared/components/ui/Text/TruncatedText";
import CustomCommonTooltip from "@/shared/components/ui/Upload/ToolTip/CustomCommonTooltip";
import CommonDropdownMenu from "@/shared/components/ui/DropDown/DropdownMenu";
import { getPersonCompany, getPersonFullName, getPersonLocation, emailQuality } from "../../utils/prospectUtils";
import type { ProspectItem } from "../../types";

const CommonAvatarComponent = CommonAvatar as React.ComponentType<any>;
const SecondaryButtonComponent = SecondaryButton as React.ComponentType<any>;
const TruncatedTextComponent = TruncatedText as React.ComponentType<any>;
const CustomCommonTooltipComponent = CustomCommonTooltip as React.ComponentType<any>;
const CommonDropdownMenuComponent = CommonDropdownMenu as React.ComponentType<any>;

const Bar = ({ className }: { className: string }) => (
  <div className={`h-3.5 rounded bg-gray-200 dark:bg-gray-800 animate-pulse ${className}`} />
);

/** Placeholder sized and aligned like the real cell in that column. */
export const ProspectSkeletonCell = ({ columnKey }: { columnKey: string }) => {
  switch (columnKey) {
    case "name":
      return (
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-800 animate-pulse shrink-0" />
          <Bar className="w-28" />
        </div>
      );
    case "actions":
      return (
        <div className="flex justify-center">
          <div className="w-8 h-7 rounded-lg bg-gray-200 dark:bg-gray-800 animate-pulse" />
        </div>
      );
    case "contact_info":
      return <Bar className="w-24" />;
    default:
      return <Bar className="w-32" />;
  }
};

const NameCell = ({ item, onOpen }: { item: ProspectItem; onOpen: (p: ProspectItem) => void }) => {
  const fullName = getPersonFullName(item);
  return (
    <div className="flex items-center justify-between group gap-2">
      <div className="flex flex-1 items-center gap-2.5 min-w-0">
        <CommonAvatarComponent
          name={fullName}
          imageUrl={item.avatar}
          size="sm"
          className="w-8 h-8 rounded-lg text-xs font-medium text-secondary shrink-0 border-2 border-gray-100 bg-[#F2FCFF]"
        />
        <div className="flex flex-col min-w-0">
          <TruncatedTextComponent text={fullName} className="text-sm font-medium text-gray-900 dark:text-gray-100" />
          {item.linkedin_url && (
            <a
              href={item.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-secondary transition-colors w-fit"
            >
              <Linkedin size={11} className="shrink-0" />
              <span>LinkedIn Profile</span>
            </a>
          )}
        </div>
      </div>
      <span className="group-hover:opacity-100 opacity-0 transition-opacity duration-300 shrink-0">
        <SecondaryButtonComponent
          title=""
          aria-label={`Open ${fullName}`}
          className="!w-8 !h-8 !min-w-0 !p-0 !gap-0 rounded-lg flex items-center justify-center border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
          icon={<ChevronsUpDown className="w-4 h-4 text-gray-500" />}
          onClick={() => onOpen(item)}
        />
      </span>
    </div>
  );
};

const TwoLineCell = ({ primary, secondary }: { primary: string; secondary?: React.ReactNode }) => (
  <div className="flex flex-col min-w-0">
    <TruncatedTextComponent text={primary} className="text-sm font-normal text-gray-800 dark:text-gray-200" />
    {typeof secondary === "string" ? (
      <TruncatedTextComponent text={secondary} className="text-xs text-gray-400" />
    ) : (
      secondary
    )}
  </div>
);

const Chip = ({ tone, icon, label, hint }: { tone: string; icon: React.ReactNode; label: string; hint: string }) => (
  <CustomCommonTooltipComponent content={hint}>
    <span className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap px-2 py-0.5 rounded-md text-[11px] border ${tone}`}>
      {icon}
      {label}
    </span>
  </CustomCommonTooltipComponent>
);

const CHIP_TONES = {
  verified: "bg-success-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/60",
  unverified: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800/60",
  pending: "bg-secondary-light-transparent text-secondary border-secondary dark:bg-secondary-dark-transparent dark:text-secondary dark:border-secondary",
  phone: "bg-secondary-light-transparent text-secondary border-secondary dark:bg-secondary-dark-transparent dark:text-secondary dark:border-secondary",
};

/** Short, single-line chips; the full meaning is in the tooltip. */
const ContactInfoCell = ({ item }: { item: ProspectItem }) => {
  // From the availability check: what import will reveal (null/undefined = not checked).
  const available = item.availability;
  const hasEmail = Boolean(item.email) || available?.email === true;
  const hasPhone = Boolean(item.phone) || available?.phone === true;
  const quality = emailQuality(item);
  const emailHint = item.email
    ? "Work email added"
    : available?.email
      ? quality === "verified"
        ? "Verified work email. Revealed when you add this contact."
        : quality === "unverified"
          ? "Work email found but not verified, so it may bounce. Revealed when you add this contact."
          : "Work email found. Revealed when you add this contact."
      : "Work email is revealed when you add this contact.";
  // Wraps instead of clipping when Email and Phone do not fit on one line.
  return (
    <div className="flex flex-wrap items-center gap-1.5 min-w-0">
      {(hasEmail || !available) && (
        <Chip
          tone={!hasEmail ? CHIP_TONES.pending : quality === "unverified" && !item.email ? CHIP_TONES.unverified : CHIP_TONES.verified}
          icon={<Mail size={10} className="text-secondary" />}
          label="Email"
          hint={emailHint}
        />
      )}
      {hasPhone && (
        <Chip
          tone={CHIP_TONES.phone}
          icon={<Phone size={10} className="text-secondary" />}
          label="Phone"
          hint={item.phone ? "Phone number available" : "Phone number available, revealed on import"}
        />
      )}
    </div>
  );
};

interface ImportCellProps {
  item: ProspectItem;
  isImported: boolean;
  isImporting: boolean;
  onImport: (p: ProspectItem) => void;
  onImportToCampaign: (p: ProspectItem) => void;
}

/** "Imported" once done; otherwise a ⋯ menu: Import to Contacts / Import to campaign. */
const ImportCell = ({ item, isImported, isImporting, onImport, onImportToCampaign }: ImportCellProps) => {
  if (isImported) {
    return (
      <div className="flex items-center justify-center">
        <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-emerald-600 dark:text-emerald-400">
          <Check size={14} /> Imported
        </span>
      </div>
    );
  }
  if (isImporting) {
    return (
      <div className="flex items-center justify-center" aria-label="Importing">
        <Loader2 size={16} className="animate-spin text-secondary" />
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center">
      <CommonDropdownMenuComponent
        ariaLabel={`Import ${getPersonFullName(item)}`}
        className="min-w-[180px]"
        actions={[
          {
            id: "contacts",
            name: "Import to Contacts",
            icon: <UserPlus size={14} className="text-gray-600" />,
            onClick: () => onImport(item),
          },
          {
            id: "campaign",
            name: "Import to campaign",
            icon: <Target size={14} className="text-gray-600" />,
            onClick: () => onImportToCampaign(item),
          },
        ]}
        triggerBtn={
          <button
            type="button"
            aria-label={`Import options for ${getPersonFullName(item)}`}
            className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <MoreHorizontal size={16} />
          </button>
        }
      />
    </div>
  );
};

export interface ProspectCellContext {
  importedIds: Set<string | number>;
  importingIds: Set<string | number>;
  onOpen: (p: ProspectItem) => void;
  onImport: (p: ProspectItem) => void;
  onImportToCampaign: (p: ProspectItem) => void;
}

/** renderCell for CustomTable: one switch, each cell its own small component. */
export const renderProspectCell = (item: ProspectItem, columnKey: string, ctx: ProspectCellContext) => {
  if (item._isSkeleton) return <ProspectSkeletonCell columnKey={columnKey} />;

  switch (columnKey) {
    case "name":
      return <NameCell item={item} onOpen={ctx.onOpen} />;
    case "title":
      return (
        <TwoLineCell
          primary={item.title || item.headline || "—"}
          secondary={
            item.closest_match ? (
              <CustomCommonTooltipComponent content={item.match_note || "Closest match to the title you searched for"}>
                <span className="text-[11px] font-medium text-amber-700 dark:text-amber-400">Closest match</span>
              </CustomCommonTooltipComponent>
            ) : item.title ? (
              item.headline
            ) : undefined
          }
        />
      );
    case "company":
      return <TwoLineCell primary={getPersonCompany(item)} />;
    case "industry":
      return (
        <TruncatedTextComponent
          text={item.organization?.industry || item.industry || "—"}
          className="text-sm text-gray-600 dark:text-gray-300"
        />
      );
    case "location":
      return (
        <div className="flex items-center gap-1.5 min-w-0 text-sm text-gray-600 dark:text-gray-300">
          <MapPin size={13} className="text-secondary shrink-0" />
          <TruncatedTextComponent text={getPersonLocation(item)} />
        </div>
      );
    case "contact_info":
      return <ContactInfoCell item={item} />;
    case "actions":
      return (
        <ImportCell
          item={item}
          isImported={ctx.importedIds.has(item.id)}
          isImporting={ctx.importingIds.has(item.id)}
          onImport={ctx.onImport}
          onImportToCampaign={ctx.onImportToCampaign}
        />
      );
    default:
      return <span className="text-sm text-gray-600 dark:text-gray-300">—</span>;
  }
};
